import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { GENDER_OPTIONS } from '../../../constants';
import getArmName from '../../../helpers/engToArm';
import { getEngName } from '../../../helpers/translit';
import { isArmenianLanguage } from '../../../helpers/langHelpers';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import DateInputFieldWithValidation from '../../ValidationFormElements/DateInputFieldWithValidation';
import SelectWithValidation from '../../ValidationFormElements/SelectWithValidation';
import useRegisterValidationFields from '../../../hooks/useRegisterValidationFields';

const PersonalInfo = ({ formProps, initialData, birthdayMaxDate, hasLanguageSensitiveNames }) => {
  const { t, i18n } = useTranslation();
  const { register, setValue, errors, clearError, getValues, triggerValidation } = formProps;

  const [birthday, setBirthday] = useState();
  const [gender, setGender] = useState();
  const [tempFirstName, setTempFirstName] = useState('');
  const [tempLastName, setTempLastName] = useState('');
  const [tempFirstNameEn, setTempFirstNameEn] = useState('');
  const [tempLastNameEn, setTempLastNameEn] = useState('');

  const handleFirstNameChange = useCallback(
    (value) => {
      setValue('firstName', value, !!value);
      setTempFirstName(value);
    },
    [setValue, setTempFirstName],
  );

  const handleLastNameChange = useCallback(
    (value) => {
      setValue('lastName', value, !!value);
      setTempLastName(value);
    },
    [setValue, setTempLastName],
  );

  const handleFirstNameEnChange = useCallback(
    (value) => {
      setValue('firstNameEn', value, !!value);
      setTempFirstNameEn(value);
    },
    [setValue, setTempFirstNameEn],
  );

  const handleLastNameEnChange = useCallback(
    (value) => {
      setValue('lastNameEn', value, !!value);
      setTempLastNameEn(value);
    },
    [setValue, setTempLastNameEn],
  );

  const handleFirstNameChangeWithTranslation = (value) => {
    handleFirstNameChange(value);
    handleFirstNameEnChange(getEngName(value));
  };

  const handleLastNameChangeWithTranslation = (value) => {
    handleLastNameChange(value);
    handleLastNameEnChange(getEngName(value));
  };

  const handleFirstNameEnChangeWithTranslation = (value) => {
    handleFirstNameEnChange(value);
    handleFirstNameChange(getArmName(value));
  };

  const handleLastNameEnChangeWithTranslation = (value) => {
    handleLastNameEnChange(value);
    handleLastNameChange(getArmName(value));
  };

  useRegisterValidationFields(['birthday', 'gender', 'firstName', 'firstNameEn', 'lastName', 'lastNameEn'], register);

  useEffect(() => {
    if (getValues().firstName) {
      triggerValidation({ name: 'firstName' });
    }
    if (getValues().lastName) {
      triggerValidation({ name: 'lastName' });
    }

    if (getValues().firstNameEn) {
      triggerValidation({ name: 'firstNameEn' });
    }
    if (getValues().lastNameEn) {
      triggerValidation({ name: 'lastNameEn' });
    }
  }, [getValues, triggerValidation]);

  useEffect(() => {
    const {
      firstName,
      lastName,
      firstNameEn,
      lastNameEn,
      birthday: bday,
      gender: gend,
    } = initialData;

    handleFirstNameChange(firstName);
    handleLastNameChange(lastName);
    handleFirstNameEnChange(firstNameEn);
    handleLastNameEnChange(lastNameEn);

    if (bday) {
      setValue('birthday', bday, true);
      setBirthday(bday);
    } else {
      setValue('birthday', null);
      setBirthday(null);
    }

    const selectedGender = GENDER_OPTIONS.find((g) => g.value === gend);
    if (selectedGender) {
      setGender(selectedGender);
      setValue('gender', gend, true);
    } else {
      setGender(null);
      setValue('gender', null);
    }
  }, [
    initialData,
    setValue,
    handleFirstNameChange,
    handleLastNameChange,
    handleFirstNameEnChange,
    handleLastNameEnChange,
  ]);

  return (
    <Grid container spacing={4}>
      {hasLanguageSensitiveNames && isArmenianLanguage(i18n.language) ? (
        <>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.firstName"
              name="firstName"
              value={tempFirstName}
              handleChange={(e) => handleFirstNameChangeWithTranslation(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.lastName"
              name="lastName"
              value={tempLastName}
              handleChange={(e) => handleLastNameChangeWithTranslation(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.firstNameEn"
              name="firstNameEn"
              value={tempFirstNameEn}
              handleChange={(e) => handleFirstNameEnChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.lastNameEn"
              name="lastNameEn"
              value={tempLastNameEn}
              handleChange={(e) => handleLastNameEnChange(e.target.value)}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.firstNameEn"
              name="firstNameEn"
              value={tempFirstNameEn}
              handleChange={(e) => handleFirstNameEnChangeWithTranslation(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.lastNameEn"
              name="lastNameEn"
              value={tempLastNameEn}
              handleChange={(e) => handleLastNameEnChangeWithTranslation(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.firstName"
              name="firstName"
              value={tempFirstName}
              handleChange={(e) => handleFirstNameChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="personalInfo.lastName"
              name="lastName"
              value={tempLastName}
              handleChange={(e) => handleLastNameChange(e.target.value)}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} md={6}>
        <DateInputFieldWithValidation
          formProps={{ errors, clearError }}
          labelKey="personalInfo.dob"
          name="birthday"
          value={birthday}
          handleChange={(date) => {
            setBirthday(date);
            setValue('birthday', date, true);
            if (getValues().socCard || errors.socCard) {
              triggerValidation({ name: 'socCard' });
            }
          }}
          maxDate={birthdayMaxDate}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SelectWithValidation
          required
          name="gender"
          label="personalInfo.gender"
          value={gender}
          formProps={formProps}
          getMenuLabel={(option) => t(option.label)}
          options={GENDER_OPTIONS}
          onChange={(newGender) => {
            setGender(newGender);
            setValue('gender', newGender.value, true);
            if (getValues().socCard) {
              triggerValidation({ name: 'socCard' });
            }
          }}
        />
      </Grid>
    </Grid>
  );
};

PersonalInfo.propTypes = {
  initialData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    firstNameEn: PropTypes.string,
    lastNameEn: PropTypes.string,
    socCard: PropTypes.string,
    birthday: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    gender: PropTypes.number,
    clearError: PropTypes.func,
  }).isRequired,
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    triggerValidation: PropTypes.func,
    errors: PropTypes.shape({
      socCard: PropTypes.shape({}),
    }),
    clearError: PropTypes.func,
  }).isRequired,
  birthdayMaxDate: PropTypes.instanceOf(Date),
  hasLanguageSensitiveNames: PropTypes.bool,
};

PersonalInfo.defaultProps = {
  birthdayMaxDate: new Date(),
  hasLanguageSensitiveNames: false,
};

export default PersonalInfo;
